import { createContext, useContext, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { UnsplashImageInterface } from 'hb-react/backoffice/components/ImageLibrary/types';

type Image = File | UnsplashImageInterface | string;

export interface SourceProviderProps {
  children: React.ReactNode;
  imageSize: string;
  initial: string;
  isPendingChecks?: boolean;
  networkId: number;
  onClose: () => void;
  onSave: (args: { image?: Image; isUpload?: boolean }) => Promise<void>;
  onUpload: (args: { file: File }) => Promise<string>;
}

// TODO: type with pintura types
interface ConfigInterface {
  [key: string]: any;
}

type Config = ConfigInterface | undefined | null;

interface SourceProviderValue
  extends Omit<SourceProviderProps, 'children' | 'initial'> {
  config?: Config;
  setConfig: Dispatch<SetStateAction<Config>>;

  imageSize: string;

  file?: Image;
  setFile: (file: Image) => void;

  temp?: string;
  setTemp: Dispatch<SetStateAction<string | undefined>>;
}

const SourceContext = createContext<SourceProviderValue>({
  networkId: 0,
  onClose: () => {},
  onSave: async () => {},
  // eslint-disable-next-line require-await
  onUpload: async () => '',

  config: undefined,
  setConfig: () => {},

  imageSize: '2000x2000',

  file: undefined,
  setFile: () => {},

  temp: '',
  setTemp: () => {},
});

export const SourceProvider = ({
  children,
  imageSize,
  initial = '',
  isPendingChecks,
  networkId,
  onClose,
  onSave,
  onUpload,
}: SourceProviderProps) => {
  const [config, setConfig] = useState<Config>();
  const [file, setFile] = useState<Image>();
  const [temp, setTemp] = useState<string | undefined>(initial);

  const value = useMemo(
    () => ({
      networkId,
      isPendingChecks,

      config,
      setConfig,

      imageSize,

      file,
      setFile,

      temp,
      setTemp,

      onClose,
      onSave,
      onUpload,
    }),
    [config, file, imageSize, temp, isPendingChecks],
  );

  return (
    <SourceContext.Provider value={value}>{children}</SourceContext.Provider>
  );
};

export const useSourceContext = () => {
  const context = useContext(SourceContext);

  if (!context) {
    throw new Error('useSourceContext must be used within a SourceProvider');
  }

  return context;
};
